import React from "react"
import {Controller, useFormContext} from "react-hook-form"
import {Input, Select, Checkbox, Label} from "@irati/ui/src/FormItem"
import {Hr} from "@irati/ui/src/Layout"
import Spacing from "@irati/ui/src/Spacing"
import Flex from "@irati/ui/src/Flex"
import {CheckoutFormContent, titleOptions, dayOptions, monthOptions, yearOptions} from "./formData"
import type {FormData} from "./index"
import styled, {css} from "styled-components"

type FieldNames = keyof FormData
type DetailsField = {label?: string; name: FieldNames; placeholder?: string}

const detailsFields: Record<FieldNames, DetailsField> = CheckoutFormContent.filter((f) =>
  ["input", "select", "checkbox"].includes(f.component)
).reduce(
  (acc, f) => {
    acc[f.name as FieldNames] = {label: f.label, name: f.name as FieldNames, placeholder: f.placeholder}
    return acc
  },
  {} as Record<FieldNames, DetailsField>
)

export const CheckoutFormFields: React.FC = () => {
  const {
    control,
    formState: {errors}
  } = useFormContext<FormData>()

  return (
    <FormContainer>
      <Spacing padding={3}>
        <Flex direction="column" gap={2}>
          <Flex wrap="wrap">
            <FormField minWidth="250px">
              <Controller
                name={detailsFields.placeOfWork!.name}
                control={control}
                render={({field}) => (
                  <Input
                    {...detailsFields.placeOfWork!}
                    label={detailsFields.placeOfWork!.label || ""}
                    value={String(field.value ?? "")}
                    onChange={field.onChange}
                    error={errors.placeOfWork?.message}
                  />
                )}
              />
            </FormField>
            <FormField minWidth="250px">
              <Controller
                name={detailsFields.employeeNumber!.name}
                control={control}
                render={({field}) => (
                  <Input
                    {...detailsFields.employeeNumber!}
                    label={detailsFields.employeeNumber!.label || ""}
                    value={String(field.value ?? "")}
                    onChange={field.onChange}
                    error={errors.employeeNumber?.message}
                  />
                )}
              />
            </FormField>
          </Flex>
          <Flex direction="column">
            <Controller
              name={detailsFields.niNumber!.name}
              control={control}
              render={({field}) => (
                <Input
                  {...detailsFields.niNumber!}
                  label={detailsFields.niNumber!.label || ""}
                  value={String(field.value ?? "")}
                  onChange={field.onChange}
                  error={errors.niNumber?.message}
                />
              )}
            />
          </Flex>
          <Controller
            name={detailsFields.isTemporaryNI.name}
            control={control}
            render={({field}) => (
              // @ts-ignore
              <Checkbox name={detailsFields.isTemporaryNI.name} checked={field.value} onChange={field.onChange}>
                {detailsFields.isTemporaryNI.label}
              </Checkbox>
            )}
          />
          <Hr />
          <Flex wrap="wrap">
            <FormField flex="0 0 150px">
              <Controller
                name={detailsFields.title.name}
                control={control}
                render={({field}) => (
                  // @ts-ignore
                  <Select {...detailsFields.title} value={field.value} onChange={field.onChange} options={titleOptions} placeholder="" />
                )}
              />
            </FormField>
            <FormField flex="1" minWidth="200px">
              <Controller
                name={detailsFields.firstName.name}
                control={control}
                render={({field}) => (
                  <Input
                    {...detailsFields.firstName}
                    label={detailsFields.firstName.label || ""}
                    value={String(field.value ?? "")}
                    onChange={field.onChange}
                    error={errors.firstName?.message}
                  />
                )}
              />
            </FormField>
            <FormField flex="1" minWidth="200px">
              <Controller
                name={detailsFields.lastName.name}
                control={control}
                render={({field}) => (
                  <Input
                    {...detailsFields.lastName}
                    label={detailsFields.lastName.label || ""}
                    value={String(field.value ?? "")}
                    onChange={field.onChange}
                    error={errors.lastName?.message}
                  />
                )}
              />
            </FormField>
          </Flex>
          <Flex direction="column">
            <DOBLabel>Date of birth*</DOBLabel>
          </Flex>
          <Flex wrap="wrap">
            <FormField flex="1" minWidth="120px">
              <Controller
                name={detailsFields.dobDay.name}
                control={control}
                render={({field}) => (
                  // @ts-ignore
                  <Select
                    label=""
                    name={detailsFields.dobDay.name}
                    value={field.value}
                    onChange={field.onChange}
                    options={dayOptions}
                    placeholder="Day"
                  />
                )}
              />
            </FormField>
            <FormField flex="1" minWidth="120px">
              <Controller
                name={detailsFields.dobMonth.name}
                control={control}
                render={({field}) => (
                  // @ts-ignore
                  <Select
                    label=""
                    name={detailsFields.dobMonth.name}
                    value={field.value}
                    onChange={field.onChange}
                    options={monthOptions}
                    placeholder="Month"
                  />
                )}
              />
            </FormField>
            <FormField flex="1" minWidth="120px">
              <Controller
                name={detailsFields.dobYear.name}
                control={control}
                render={({field}) => (
                  // @ts-ignore
                  <Select
                    label=""
                    name={detailsFields.dobYear.name}
                    value={field.value}
                    onChange={field.onChange}
                    options={yearOptions}
                    placeholder="Year"
                  />
                )}
              />
            </FormField>
          </Flex>
          <Flex wrap="wrap">
            <FormField flex="1" minWidth="250px">
              <Controller
                name={detailsFields.email.name}
                control={control}
                render={({field}) => (
                  <Input
                    {...detailsFields.email}
                    label={detailsFields.email.label || ""}
                    value={String(field.value ?? "")}
                    onChange={field.onChange}
                    error={errors.email?.message}
                  />
                )}
              />
            </FormField>
            <FormField flex="1" minWidth="250px">
              <Controller
                name={detailsFields.mobileNumber.name}
                control={control}
                render={({field}) => (
                  <Input
                    {...detailsFields.mobileNumber}
                    label={detailsFields.mobileNumber.label || ""}
                    value={String(field.value ?? "")}
                    onChange={field.onChange}
                    error={errors.mobileNumber?.message}
                  />
                )}
              />
            </FormField>
          </Flex>
          <Hr />
          <Controller
            name={detailsFields.addressLine1.name}
            control={control}
            render={({field}) => (
              <Input
                {...detailsFields.addressLine1}
                label={detailsFields.addressLine1.label || ""}
                value={String(field.value ?? "")}
                onChange={field.onChange}
                error={errors.addressLine1?.message}
              />
            )}
          />
          <Controller
            name={detailsFields.addressLine2.name}
            control={control}
            render={({field}) => (
              <Input
                {...detailsFields.addressLine2}
                label={detailsFields.addressLine2.label || ""}
                value={String(field.value ?? "")}
                onChange={field.onChange}
                error={errors.addressLine2?.message}
              />
            )}
          />
          <Flex wrap="wrap">
            <FormField flex="1" minWidth="150px">
              <Controller
                name={detailsFields.town.name}
                control={control}
                render={({field}) => (
                  <Input
                    {...detailsFields.town}
                    label={detailsFields.town.label || ""}
                    value={String(field.value ?? "")}
                    onChange={field.onChange}
                    error={errors.town?.message}
                  />
                )}
              />
            </FormField>
            <FormField flex="1" minWidth="150px">
              <Controller
                name={detailsFields.county.name}
                control={control}
                render={({field}) => (
                  <Input
                    {...detailsFields.county}
                    label={detailsFields.county.label || ""}
                    value={String(field.value ?? "")}
                    onChange={field.onChange}
                    error={errors.county?.message}
                  />
                )}
              />
            </FormField>
            <FormField flex="1" minWidth="150px">
              <Controller
                name={detailsFields.postcode.name}
                control={control}
                render={({field}) => (
                  <Input
                    {...detailsFields.postcode}
                    label={detailsFields.postcode.label || ""}
                    value={String(field.value ?? "")}
                    onChange={field.onChange}
                    error={errors.postcode?.message}
                    style={{textTransform: "uppercase"}}
                  />
                )}
              />
            </FormField>
          </Flex>
        </Flex>
      </Spacing>
    </FormContainer>
  )
}

const FormContainer = styled.div`
  max-width: 648px;
`

const FormField = styled.div<{flex?: string; minWidth?: string}>`
  ${({flex, minWidth}) => css`
    flex: ${flex || "1"};
    ${minWidth && `min-width: ${minWidth};`}
  `}
`

const DOBLabel = styled(Label)`
  ${({theme}) => css`
    padding-left: ${theme.spacing.two};
  `}
`
